import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, LineChart, Activity, Sparkles, Edit2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import NutritionGraphs from '../components/NutritionGraphs';
import SoftBlurWrapper from '../components/SoftBlurWrapper';
import { getNutritionRecords, createNutritionRecord, updateNutritionRecord, deleteNutritionRecord, getAllTreatments, getBloodResults } from '../utils/api';
import { formatDisplayDate } from '../utils/dateUtils';
import useUsageLimit from '../hooks/useUsageLimit';

const NutritionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isReadOnly, targetUser, readonlyToken } = useReadOnly();
  const displayUser = isReadOnly ? targetUser : user;
  const userCountry = displayUser?.country || 'AU';
  const [records, setRecords] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [bloodResults, setBloodResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Usage limit for soft blur gating
  const { isFreeLimitReached, loading: usageLoading, refetch: refetchUsage } = useUsageLimit();
  
  // Edit state - NO formKey, simple boolean
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    vitamin_d: '',
    b12: '',
    folate: '',
    ferritin: '',
    notes: ''
  });

  const getMarkerConfig = (markerId) => {
    const isUS = user?.country === 'US';
    const configs = {
      vitamin_d: { 
        label: 'Vitamin D', 
        unit: isUS ? 'ng/mL' : 'nmol/L',
        range: isUS ? '20-50' : '50-125'
      },
      b12: { 
        label: 'B12', 
        unit: isUS ? 'pg/mL' : 'pmol/L',
        range: isUS ? '200-900' : '150-700'
      },
      folate: { 
        label: 'Folate', 
        unit: isUS ? 'ng/mL' : 'nmol/L',
        range: isUS ? '3-20' : '7-45'
      },
      ferritin: { 
        label: 'Ferritin', 
        unit: 'ng/mL',
        range: '30-300'
      }
    };
    return configs[markerId];
  };

  const markers = {
    vitamin_d: getMarkerConfig('vitamin_d'),
    b12: getMarkerConfig('b12'),
    folate: getMarkerConfig('folate'),
    ferritin: getMarkerConfig('ferritin')
  };

  useEffect(() => {
    fetchRecords();
    fetchTreatments();
    fetchBloodResults();
  }, [readonlyToken]); // Re-fetch when entering/exiting readonly mode

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const data = await getNutritionRecords();
      setRecords(data);
    } catch (error) {
      toast.error('Failed to load nutrition records');
    } finally {
      setLoading(false);
    }
  };

  const fetchTreatments = async () => {
    try {
      const data = await getAllTreatments();
      setTreatments(data);
    } catch (error) {
      console.error('Failed to load treatments');
      setTreatments([]);
    }
  };

  const fetchBloodResults = async () => {
    try {
      const data = await getBloodResults();
      setBloodResults(data);
    } catch (error) {
      console.error('Failed to load blood results for timeline');
      setBloodResults([]);
    }
  };

  // TOP-LEVEL EDIT HANDLER - No effects, no reactive deps
  const handleEdit = async (record) => {
    console.log('🔵 Nutrition edit clicked for:', record.id);
    
    try {
      // Fetch fresh data from backend
      const freshData = await getNutritionRecords();
      const freshRecord = freshData.find(r => r.id === record.id);
      
      if (!freshRecord) {
        toast.error('Record not found');
        return;
      }
      
      // Set editing state
      setEditingId(freshRecord.id);
      setEditFormData({
        date: freshRecord.date,
        vitamin_d: freshRecord.vitamin_d !== null ? freshRecord.vitamin_d.toString() : '',
        b12: freshRecord.b12 !== null ? freshRecord.b12.toString() : '',
        folate: freshRecord.folate !== null ? freshRecord.folate.toString() : '',
        ferritin: freshRecord.ferritin !== null ? freshRecord.ferritin.toString() : '',
        notes: freshRecord.notes || ''
      });
      setIsEditing(true);
      
      console.log('✅ Nutrition edit state set successfully');
    } catch (error) {
      console.error('Nutrition edit failed:', error);
      toast.error('Failed to load record for editing');
    }
  };

  // TOP-LEVEL SAVE HANDLER
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    console.log('💾 Saving nutrition edit for:', editingId);
    
    // Display unit -> canonical unit factors (US only; AU/other display units ARE canonical)
    const TO_CANONICAL_US = { vitamin_d: 2.5, b12: 0.738, folate: 2.266, ferritin: 1 };
    const isUS = user?.country === 'US';
    
    // Build payload in CANONICAL units (same conversion as the add flow)
    const payload = { date: editFormData.date, units: 'canonical' };
    let hasValue = false;
    
    Object.keys(markers).forEach(key => {
      if (editFormData[key] !== '') {
        const displayValue = parseFloat(editFormData[key]);
        const canonicalValue = isUS ? displayValue * TO_CANONICAL_US[key] : displayValue;
        payload[key] = parseFloat(canonicalValue.toFixed(2));
        hasValue = true;
      }
    });

    if (editFormData.notes) {
      payload.notes = editFormData.notes;
    }

    if (!hasValue) {
      toast.error('Please enter at least one value');
      return;
    }

    try {
      // HTTP PUT request
      await updateNutritionRecord(editingId, payload);
      console.log('✅ Nutrition save successful');
      toast.success('Nutrition record updated');
      
      // Close modal
      setIsEditing(false);
      
      // Explicit refetch
      console.log('🔄 Refetching nutrition records...');
      await fetchRecords();
      
      // Reset state explicitly
      setEditingId(null);
      setEditFormData({
        date: '',
        vitamin_d: '',
        b12: '',
        folate: '',
        ferritin: '',
        notes: ''
      });
      
      console.log('✅ Nutrition edit cycle complete');
    } catch (error) {
      console.error('Nutrition save failed:', error);
      toast.error('Failed to update record');
    }
  };

  // TOP-LEVEL CANCEL HANDLER
  const handleCancelEdit = () => {
    console.log('❌ Nutrition edit cancelled');
    setIsEditing(false);
    setEditingId(null);
    setEditFormData({
      date: '',
      vitamin_d: '',
      b12: '',
      folate: '',
      ferritin: '',
      notes: ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      await deleteNutritionRecord(id);
      toast.success('Record deleted');
      fetchRecords();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading nutrition records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">Nutrition & Deficiencies</h1>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700">Track vitamins and minerals</p>
        </div>
        <div className="flex">
          <Button
            onClick={() => navigate('/nutrition/add-step-by-step')}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm sm:text-base"
          >
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
            Step-by-Step Entry
          </Button>
        </div>
      </div>

      {/* EDIT MODAL - NO key prop */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-slate-800">Edit Nutrition Record</h2>
              <button
                onClick={handleCancelEdit}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSaveEdit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Test Date</label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(markers).map(([key, marker]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {marker.label} ({marker.unit})
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={editFormData[key]}
                      onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                      placeholder="Optional"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Reference: {marker.range} {marker.unit}
                    </p>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Any relevant notes..."
                />
              </div>

              <div className="flex gap-4">
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
                <button type="button" onClick={handleCancelEdit} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
        <div className="flex gap-3">
          <Activity className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">About Nutrition Tracking</p>
            <p className="text-sm text-blue-800 mt-1">
              These markers track reversible deficiencies influenced by diet and supplements. 
              They are separate from your CBC blood cancer monitoring and do not trigger disease alerts.
            </p>
          </div>
        </div>
      </div>

      {/* Vitamin Trends Section - Tabs + Chart + Timeline (all aligned) */}
      {records.length > 0 && (
        <SoftBlurWrapper isBlurred={isFreeLimitReached && !usageLoading} className="mb-8">
          <NutritionGraphs 
            records={records} 
            supplements={treatments}
          />
        </SoftBlurWrapper>
      )}

      {/* Records List */}
      <h2 className="text-2xl font-bold text-slate-900 mb-6">
        {records.length > 0 ? 'History' : 'No Records Yet'}
      </h2>
      {records.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
          <LineChart className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No nutrition records yet</p>
          <p className="text-sm text-slate-500 mt-2">
            Add your first record to start tracking vitamins and minerals
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-slate-300 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-900">
                    {formatDisplayDate(record.date, userCountry)}
                  </span>
                </div>
                <div className="flex gap-2">
                  {/* EDIT BUTTON - Nutrition ONLY */}
                  <button
                    onClick={() => handleEdit(record)}
                    data-testid={`edit-nutrition-${record.id}`}
                    className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                    title="Edit record"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(record.id)}
                    data-testid={`delete-nutrition-${record.id}`}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete record"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(markers).map(([key, marker]) => (
                  <div key={key}>
                    <p className="text-xs text-slate-500">{marker.label}</p>
                    {record[key] ? (
                      <p className="text-lg font-semibold text-slate-900">
                        {record[key]} <span className="text-sm text-slate-500">{marker.unit}</span>
                      </p>
                    ) : (
                      <p className="text-sm text-slate-400">Not recorded</p>
                    )}
                  </div>
                ))}
              </div>

              {record.notes && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600">{record.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NutritionPage;
